import React, { useState, useEffect } from "react";
import { Box, Switch, Typography, Divider, TextField, Button, Alert, Toolbar } from "@mui/material";
import { getConfig, saveConfig } from "../api/agentApi";
import { useFeatureFlags } from "../context/FeatureFlagContext.tsx";
import { useUser } from "./UserContext";

export default function ConfigPage() {
  const [hostPath, setHostPath] = useState("");
  const [savedHostPath, setSavedHostPath] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const { flags, setFlag } = useFeatureFlags();
  const { user } = useUser();
  
  // Local state for pending feature flag changes
  const [pendingFlags, setPendingFlags] = useState(flags);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Fetch current config
    getConfig().then(cfg => {
      setHostPath(cfg.host_path || "");
      setSavedHostPath(cfg.host_path || "");
    });
  }, []);

  // Sync pendingFlags when flags change (e.g., on initial load from localStorage)
  useEffect(() => {
    setPendingFlags(flags);
  }, [flags]);

  useEffect(() => {
    // Check if there are unsaved changes
    const flagKeys = Object.keys(flags);
    const flagsChanged = flagKeys.some(key => pendingFlags[key] !== flags[key]);
    const hostPathChanged = hostPath !== savedHostPath;
    setHasUnsavedChanges(flagsChanged || hostPathChanged);
  }, [pendingFlags, flags, hostPath, savedHostPath]);

  const handleFlagChange = (key, value) => {
    setPendingFlags(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setMsg("");
    
    try {
      let saveSuccess = true;
      
      // Save host path only if it changed
      if (hostPath !== savedHostPath) {
        const data = await saveConfig({ host_path: hostPath });
        if (data.status === "success") {
          setSavedHostPath(hostPath);
        } else {
          saveSuccess = false;
          setMsg("Failed to save host path.");
        }
      }
      
      // Apply feature flag changes
      const flagKeys = Object.keys(flags);
      const hasFlgChanges = flagKeys.some(key => pendingFlags[key] !== flags[key]);
      
      if (hasFlgChanges) {
        flagKeys.forEach(key => {
          if (pendingFlags[key] !== flags[key]) {
            setFlag(key, pendingFlags[key]);
          }
        });
      }
      
      if (saveSuccess) {
        setMsg("Configuration saved successfully!");
      }
    } catch (error) {
      console.error("Save error:", error);
      setMsg("Failed to save configuration.");
    }
    
    setLoading(false);
  };

  return (
    <Box sx={{ pt: 1, px: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h4" sx={{ pl: 0 }}>Configuration</Typography>
      </Toolbar>
      <Box sx={{ pt: 1 }}>
      
      {/* Feature Flags Section */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h5" gutterBottom>Feature Flags</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Toggle feature flags and click "Save Configuration" to apply changes.
        </Typography>
        <Box sx={{ p: 2, borderRadius: 1 }}>
          <Box
            key={"key"}
            display="flex"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box sx={{ flex: 1, maxWidth: 600 }}>
              <Typography sx={{ textTransform: "capitalize" }}>{"New UI"}</Typography>
              <Typography variant="caption" color="text.secondary">
                Enable the redesigned user interface
              </Typography>
            </Box>
            <Switch
              checked={Boolean(pendingFlags["newAgentPage"])}
              onChange={(e) => handleFlagChange("newAgentPage", e.target.checked)}
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box
            key={"polling-key"}
            display="flex"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box sx={{ flex: 1, maxWidth: 600 }}>
              <Typography sx={{ textTransform: "capitalize" }}>{"Enable Auto Refresh"}</Typography>
              <Typography variant="caption" color="text.secondary">
                Automatically refresh agent runs and workflow status
              </Typography>
            </Box>
            <Switch
              checked={Boolean(pendingFlags["enablePolling"])}
              onChange={(e) => handleFlagChange("enablePolling", e.target.checked)}
            />
          </Box>
          {user?.role === 'admin' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                key={"workflows-key"}
                display="flex"
                alignItems="center"
              >
                <Box sx={{ flex: 1, maxWidth: 600 }}>
                  <Typography sx={{ textTransform: "capitalize" }}>{"Enable Workflows (Admin Only)"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Show workflow features in navigation menu (requires admin role)
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(pendingFlags["enableWorkflows"])}
                  onChange={(e) => handleFlagChange("enableWorkflows", e.target.checked)}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* RAG Config Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>RAG Config</Typography>
        <Box sx={{ mt: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Configure Retrieval-Augmented Generation (RAG) settings for your tenant here.
          </Typography>
        </Box>
      </Box>
      
      {/* Host Path Config */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>Agent Host Path</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This directory will be mounted into agent Docker containers as a shared volume.
        </Typography>
        <TextField
          label="Host Path"
          value={hostPath}
          onChange={e => setHostPath(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
        />
      </Box>

      {/* Save Button */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSave} 
          disabled={loading || !hasUnsavedChanges}
          size="large"
        >
          {loading ? "Saving..." : "Save Configuration"}
        </Button>
        {hasUnsavedChanges && !loading && (
          <Typography variant="body2" color="warning.main">
            You have unsaved changes
          </Typography>
        )}
      </Box>
      
      {msg && <Alert severity={msg.includes("saved") ? "success" : "error"} sx={{ mt: 2 }}>{msg}</Alert>}
      {savedHostPath && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          Current host path: <b>{savedHostPath}</b>
        </Typography>
      )}
      </Box>
    </Box>
  );
}
